import { useEffect } from "react";
import {
  extractChatInvestigation,
  parseChatInvestigation,
  parseChatInvestigationLink,
  serializeChatInvestigation,
  type ChatInvestigation,
} from "../../../shared/chatInvestigation";
import { restoreDraft, useDraft } from "../../lib/draft";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";

const draftScope = (id: string) => `/chat:${id}:monitoring`;

export function stageChatInvestigation(id: string, context: ChatInvestigation) {
  restoreDraft(draftScope(id), JSON.stringify(context));
}

export function useChatInvestigation(
  personalChatId: string,
  readOnly: boolean,
  hasMessages: boolean
) {
  const { tabId, monitoring } = useCurrentUrl(routes.chats);
  const fromUrl =
    !readOnly && !hasMessages && tabId === personalChatId && monitoring
      ? parseChatInvestigationLink(monitoring)
      : undefined;
  const scope = draftScope(personalChatId);
  const [value, setValue, clearStored] = useDraft(
    scope,
    fromUrl ? JSON.stringify(fromUrl) : ""
  );
  const context = readOnly ? undefined : parseChatInvestigation(value);

  // Consume the handoff once. Removal or sending must not reattach it on refresh
  // or leak it into another internal Chat tab via retained query parameters.
  useEffect(() => {
    if (tabId === personalChatId && monitoring) {
      navigate(routes.chats, { monitoring: "" }, { replace: true });
    }
  }, [tabId, personalChatId, monitoring]);

  const clear = () => {
    clearStored();
    setValue("");
  };
  const restore = (message: string, draft: string) => {
    const saved = extractChatInvestigation(message).context;
    if (saved && !readOnly) {
      const serialized = JSON.stringify(saved);
      restoreDraft(scope, serialized);
      setValue((previous) => previous || serialized);
    }
    return extractChatInvestigation(draft).remainingText;
  };
  return {
    context,
    text: context ? serializeChatInvestigation(context) : "",
    clear,
    restore,
  };
}
